<script setup lang="ts">
/**
 * @author Beka Chkhaidze
 */
import { computed, ref, onBeforeMount, onBeforeUnmount } from "vue";
import { isInView } from "@/composables/utils/isInView";
import { useMainStore } from "@/store/main/index";

import { getTaxonomySS } from "@/composables/core/func";

import type { SecondaryDataInterface } from "@/types/ContentType";
import type { FAQCategory } from "@/store/main/types/widgets-terms/terms";

import Head from "./parts/Head.vue";
import Summary from "./parts/Summary.vue";
import Chart from "./parts/Chart.vue";
import { useBuildingStore } from "@/store/building";

const props = defineProps<{
  contentData?: SecondaryDataInterface;
}>();

const buildingStore = useBuildingStore();
const mainStore = useMainStore();

await getTaxonomySS("forecast_category");

await buildingStore.getProjectLowestPrices();

const activeType = ref<FAQCategory>();
const templateRef = ref<HTMLDivElement>();
const isTemplateRefInView = ref(false);
const resetOpacity = ref(false);
const resetWidth = ref(false);

let changed = false;

const isMob = computed(() => mainStore.windowWidth <= mainStore.breakpoints.mobile);
const allTypes = computed(() => {
  // because, too lazy backed developers
  const idsFromContentData = props.contentData?.data?.list
    .map((el) => el.forecast_category)
    ?.flat()
    ?.map(Number);

  const filtered = mainStore.terms?.forecast_category?.filter((el) =>
    idsFromContentData?.includes(el.id)
  );

  activeType.value = filtered?.[0];

  return filtered;
});

const SelectedTypeData = computed(() => {
  const match = props.contentData?.data?.list?.find(
    (el) =>
      el.forecast_category?.includes(String(activeType.value?.id)) ||
      el.forecast_category?.includes(activeType.value?.id as any)
  );
  return match;
});

const onSelect = (item: FAQCategory) => {
  resetOpacity.value = true;

  setTimeout(() => {
    resetWidth.value = true;
    resetOpacity.value = false;
    activeType.value = item;
  }, 850);

  setTimeout(() => {
    resetWidth.value = false;
  }, 900);
};

const onAppScroll = () => {
  if (changed) return;

  isTemplateRefInView.value = isInView(templateRef.value!, isMob.value ? 1 : 0.9);

  if (isTemplateRefInView.value) {
    changed = true;
  }
};

const onVnodeMount = () => {
  if (changed) return;

  setTimeout(() => {
    window?.addEventListener("scroll", onAppScroll);
  }, 2000);
};

onBeforeUnmount(() => {
  window?.removeEventListener("scroll", onAppScroll);
});
</script>

<template>
  <section class="project-forecast">
    <div class="pos-rel">
      <div class="custom-container">
        <div class="project-forecast-in">
          <Head v-if="contentData?.conf?.header" :header="contentData.conf.header" />
          <Summary
            v-if="contentData?.data?.listCount"
            v-bind="{
              allTypes,
              SelectedTypeData,
              activeType,
              items: contentData?.data?.list,
            }"
            @select="onSelect"
          />
          <div ref="templateRef">
            <Chart
              :class="{ 'reset-width': resetWidth, 'reset-opacity': resetOpacity }"
              v-if="SelectedTypeData"
              v-bind="{
                isTemplateRefInView,
                SelectedTypeData,
                activeType,
              }"
              @vue:mounted="onVnodeMount"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" src="./style/projectForecast.scss" />
